import {getTestProps} from '@benchling/js-test-utils';
import {Anchor} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {openScratchPlateChipModal} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as DateUtil from 'util/DateUtil';

import {
  WellAttributionEndpoint,
  WellAttributionResponse,
  WellAttributionRow,
} from './ContainerWellAttributionConstants';
import {fetchWellAttribution} from './ContainerWellAttributionDAO';

export interface ContainerWellAttributionTableProps {
  inventoryPlateId: string;
  rowIndex: number;
  columnIndex: number;
}

interface State {
  attribution: WellAttributionResponse | null;
  hasError: boolean;
}

function AmountCell({row}: {row: WellAttributionRow}): JSX.Element {
  return (
    <Cell>
      {row.transferAmount != null
        ? `${row.transferAmount}${row.transferUnit != null ? ` ${row.transferUnit}` : ''}`
        : null}
    </Cell>
  );
}

function AuthParentCell({row}: {row: WellAttributionRow}): JSX.Element {
  if (row.authParentId == null) {
    return <Cell>Unavailable</Cell>;
  }
  return (
    <Cell>
      <BenchChip id={row.authParentId} size={ChipSize.TABLE_CELL} />
    </Cell>
  );
}

function EndpointCell({endpoint}: {endpoint: WellAttributionEndpoint}): JSX.Element {
  if (endpoint.isViewedWell) {
    return <Cell>{`${endpoint.well} (This well)`}</Cell>;
  }
  if (endpoint.inventoryWellId != null) {
    return (
      <Cell>
        <BenchChip id={endpoint.inventoryWellId} size={ChipSize.TABLE_CELL} />
      </Cell>
    );
  }
  return (
    <Cell>
      <Anchor
        component="button"
        type="button"
        onClick={() => {
          openScratchPlateChipModal({
            scratchPlateId: endpoint.plateId,
            plateName: endpoint.name,
            wellPosition: endpoint.well,
          });
        }}
      >
        {`${endpoint.name}:${endpoint.well}`}
      </Anchor>
    </Cell>
  );
}

type TransferDirection = 'from' | 'to';

interface AttributionTableProps {
  direction: TransferDirection;
  emptyTableText: string;
  isLoading: boolean;
  rows: Array<WellAttributionRow>;
  title: string;
  truncated: boolean;
}

function getEndpointKey(endpoint: WellAttributionEndpoint): string {
  return `${endpoint.plateId}:${endpoint.well}`;
}

function getLineageLabel(
  row: WellAttributionRow,
  direction: TransferDirection,
  nonTerminalEndpointKeys: Set<string>,
  truncated: boolean
): string {
  if (row.source.isViewedWell || row.destination.isViewedWell) {
    return 'Current';
  }
  const finalEndpoint = direction === 'to' ? row.source : row.destination;
  if (!truncated && !nonTerminalEndpointKeys.has(getEndpointKey(finalEndpoint))) {
    return 'Final';
  }
  return `Intermediate ${row.distance - 1}`;
}

function AttributionTable({
  direction,
  emptyTableText,
  isLoading,
  rows,
  title,
  truncated,
}: AttributionTableProps): JSX.Element {
  const nonTerminalEndpointKeys = new Set(
    rows.map((row) => getEndpointKey(direction === 'to' ? row.destination : row.source))
  );
  const orderedRows = [...rows].sort((left, right) =>
    direction === 'to' ? right.distance - left.distance : left.distance - right.distance
  );

  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <Table
        emptyTableText={isLoading ? 'Loading transfers...' : emptyTableText}
        getRowKey={(rowIndex) => {
          const row = orderedRows[rowIndex];
          return `${row.source.plateId}:${row.source.well}->${row.destination.plateId}:${row.destination.well}`;
        }}
        isLoading={isLoading}
        numRows={orderedRows.length}
      >
        <Column
          header="Plate Lineage"
          renderCell={(rowIndex) => (
            <Cell>
              {getLineageLabel(orderedRows[rowIndex], direction, nonTerminalEndpointKeys, truncated)}
            </Cell>
          )}
        />
        <Column
          header="Source"
          renderCell={(rowIndex) => <EndpointCell endpoint={orderedRows[rowIndex].source} />}
        />
        <Column
          header="Destination"
          renderCell={(rowIndex) => <EndpointCell endpoint={orderedRows[rowIndex].destination} />}
        />
        <Column header="Amount" renderCell={(rowIndex) => <AmountCell row={orderedRows[rowIndex]} />} />
        <Column
          header="Transferred"
          renderCell={(rowIndex) => (
            <Cell>{DateUtil.readableTime(orderedRows[rowIndex].transferredAt, {showTime: false})}</Cell>
          )}
        />
        <Column
          header="Automation Template"
          renderCell={(rowIndex) => <AuthParentCell row={orderedRows[rowIndex]} />}
        />
      </Table>
    </section>
  );
}

export default function ContainerWellAttributionTable({
  inventoryPlateId,
  rowIndex,
  columnIndex,
}: ContainerWellAttributionTableProps): JSX.Element {
  const [state, setState] = useState<State>({attribution: null, hasError: false});

  useEffect(() => {
    let isActive = true;
    setState({attribution: null, hasError: false});
    fetchWellAttribution(inventoryPlateId, rowIndex, columnIndex).then(
      (attribution) => {
        if (isActive) {
          setState({attribution, hasError: false});
        }
      },
      () => {
        if (isActive) {
          setState({attribution: null, hasError: true});
        }
      }
    );
    return () => {
      isActive = false;
    };
  }, [columnIndex, inventoryPlateId, rowIndex]);

  const isLoading = state.attribution == null && !state.hasError;
  const errorText = 'Unable to load well attribution. Refresh the page to try again.';

  return (
    <div {...getTestProps({component: 'ContainerWellAttributionTable', element: 'root'})}>
      <AttributionTable
        direction="to"
        emptyTableText={state.hasError ? errorText : 'No automation template transfers to this well'}
        isLoading={isLoading}
        rows={state.attribution?.transfersTo ?? []}
        title="Automation Template Transfers to Container"
        truncated={state.attribution?.truncated ?? false}
      />
      <AttributionTable
        direction="from"
        emptyTableText={state.hasError ? errorText : 'No automation template transfers from this well'}
        isLoading={isLoading}
        rows={state.attribution?.transfersFrom ?? []}
        title="Automation Template Transfers from Container"
        truncated={state.attribution?.truncated ?? false}
      />
      {state.attribution?.truncated && (
        <p className="u-faded">
          <strong>Lineage truncated.</strong> Some attribution lineage is not shown.
        </p>
      )}
    </div>
  );
}
